import {
  PatientOrderStatus,
  normalizePatientOrderStatus
} from '../PatientDashboard/Patient.model'
import {
  PatientDetailsDto,
  PatientProgressDto
} from '../../../types/swaggerTypes'

/** CLOS-13995 / Figma Frame 1338 — hardcoded until Squidex/i18n keys are added. */
export const PTA_BASELINE_COMPLETE_CHECKBOX_LABEL =
  'I confirm that consent and screening information about the patient has been recorded within the above system'

/** CLOS-13995 — intro copy above consent/baseline steps (Figma Frame 1338). */
export const PTA_CONSENT_BASELINE_INTRO_TEXT =
  'Before placing orders for post-trial access patients, you must ensure consent and screening information are captured and submitted to Clinigen.'

/** CLOS-14150 — guidance shown while patient consent is still awaiting capture. */
export const PTA_CONSENT_REFRESH_GUIDANCE =
  'Please refresh the page if you have already completed the consent process.'

/** CLOS-13424 — step 2 success notification copy. */
export const PTA_BASELINE_CAPTURED_SUCCESS_MESSAGE =
  'Consent and screening info successfully submitted you may now begin your order for this patient'

/** Shown in Step 2 when baseline verification returns null or incomplete. */
export const PTA_BASELINE_INCOMPLETE_ERROR_MESSAGE =
  'Incomplete screening information, please click through below to complete'

/** Shown in Step 2 when baseline verification fails for any unexpected reason. */
export const PTA_BASELINE_VERIFICATION_FAILED_MESSAGE =
  'We could not verify the screening information, please try again shortly'

/** Shape of a rejected HTTP call, narrowed without depending on axios here. */
type HttpErrorLike = { response?: { status?: number } }

/**
 * Only the BFF's validation response means the baseline is genuinely incomplete;
 * anything else is an unexpected failure and must not tell the user to go and
 * complete data that may already be complete.
 */
export function resolvePtaBaselineFailureMessage(error: unknown): string {
  const status =
    error && typeof error === 'object'
      ? (error as HttpErrorLike).response?.status
      : undefined

  return status === 400
    ? PTA_BASELINE_INCOMPLETE_ERROR_MESSAGE
    : PTA_BASELINE_VERIFICATION_FAILED_MESSAGE
}

type PtaBaselineProgress = Pick<PatientProgressDto, 'patientBaselineCaptured'>

export function isPtaBaselineProgressComplete(
  progress: PtaBaselineProgress
): boolean {
  return progress.patientBaselineCaptured === true
}

const PTA_BASELINE_CHECK_ELIGIBLE_STATUSES = new Set<PatientOrderStatus>([
  PatientOrderStatus.AwaitingBaselineData,
  PatientOrderStatus.BeginOrder
])

/** True when patient order status may run the CLOS-13424 baseline checkbox check. */
export function isPtaBaselineCheckEligibleStatus(
  patient: Pick<
    PatientDetailsDto,
    'patientOrderStatus' | 'patientConsentCaptured'
  >
): boolean {
  const normalizedStatus = normalizePatientOrderStatus(
    patient.patientOrderStatus
  ) as PatientOrderStatus

  return (
    !!normalizedStatus &&
    (PTA_BASELINE_CHECK_ELIGIBLE_STATUSES.has(normalizedStatus) ||
      (normalizedStatus === PatientOrderStatus.AwaitingConsent &&
        patient.patientConsentCaptured === true))
  )
}

/**
 * Statuses a PTA patient reaches only after the Step 2 baseline confirmation:
 * reaching any of them requires an order to exist, which requires baseline. The
 * whole order lifecycle is listed so a lagging patientBaselineCaptured echo
 * cannot re-open Step 2 at a later status. An unknown or unmapped status is not
 * in the set and is treated as not yet confirmed. Deliberately excluded:
 * AwaitingConsent / AwaitingBaselineData / BeginOrder (pre-order), and the administrative
 * statuses that can occur at any stage (PatientDiscontinued, ProgramClosed,
 * Reassigned, Active), which do not imply baseline was captured.
 */
const PTA_POST_BASELINE_STATUSES = new Set<PatientOrderStatus>([
  PatientOrderStatus.IncompleteCheckout,
  PatientOrderStatus.IncompletePAF,
  PatientOrderStatus.Submitted,
  PatientOrderStatus.UnderReview,
  PatientOrderStatus.UnderMedicalReview,
  PatientOrderStatus.HealthAuthorityReview,
  PatientOrderStatus.MedicalReviewDeclined,
  PatientOrderStatus.HealthAuthorityDeclined,
  PatientOrderStatus.Processing,
  PatientOrderStatus.Pending,
  PatientOrderStatus.Shipped,
  PatientOrderStatus.InTransit,
  PatientOrderStatus.OutForDelivery,
  PatientOrderStatus.Available_for_Pickup,
  PatientOrderStatus.FailedAttempt,
  PatientOrderStatus.FailedDelivery,
  PatientOrderStatus.Delivered,
  PatientOrderStatus.Delayed,
  PatientOrderStatus.ReadyForResupply,
  PatientOrderStatus.Expired,
  PatientOrderStatus.Cancelled
])

/** True when baseline confirmation should survive reload. */
export function hasPersistedPtaBaselineConfirmation(
  patient: Pick<
    PatientDetailsDto,
    'patientBaselineCaptured' | 'patientConsentCaptured' | 'patientOrderStatus'
  >
): boolean {
  if (patient.patientBaselineCaptured === true) {
    return true
  }

  if (patient.patientConsentCaptured !== true) {
    return false
  }

  const normalizedStatus = normalizePatientOrderStatus(
    patient.patientOrderStatus
  ) as PatientOrderStatus

  return PTA_POST_BASELINE_STATUSES.has(normalizedStatus)
}

/**
 * True once baseline is captured, so a stale Step 2 error must not sit above a
 * step that already reports completion (e.g. after returning from Castor EDC,
 * which reloads patient details without remounting the page).
 */
export function shouldClearPtaBaselineError(
  patient:
    | Pick<
        PatientDetailsDto,
        | 'patientBaselineCaptured'
        | 'patientConsentCaptured'
        | 'patientOrderStatus'
      >
    | undefined,
  isConsentBaselineConfirmed: boolean
): boolean {
  return (
    isConsentBaselineConfirmed ||
    (!!patient && hasPersistedPtaBaselineConfirmation(patient))
  )
}

/**
 * Show notification when getProgress confirms baseline complete (CLOS-13424).
 */
export function shouldShowPtaBaselineCompleteNotification(
  patient: Pick<
    PatientDetailsDto,
    'patientOrderStatus' | 'patientConsentCaptured'
  >,
  progress: PtaBaselineProgress,
  isPostTrialAccess: boolean
): boolean {
  if (!isPostTrialAccess) {
    return false
  }

  if (!isPtaBaselineCheckEligibleStatus(patient)) {
    return false
  }

  return isPtaBaselineProgressComplete(progress)
}
